import { OptionType } from '@evinced-private/ui-common';

import {
	buildFilterVariables,
	parseLabelsIntoFilters
} from 'src/common/helpers/GQLRequestsHelper/labelFilters';
import { separateNoOptionLabels } from 'src/common/helpers/GQLRequestsHelper/noOptionFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import { addSeparatedCustomFieldsNeTupleKeys } from 'src/common/helpers/GQLRequestsHelper/queryVariablesHelpers';

import {
	buildLabelsSearchValues,
	labelsSearchFields,
	testsSearchFields
} from '../../Tests/helpers/buildVariables';
import { UsageDataQueryVariables } from '../UsageData.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';
const DAYS_AMOUNT_X2_FILTER_PARAM_KEY = 'daysAmountx2Filter';

/**
 * Builds the base query variables common to both v1 and v2
 */
const buildBaseUsageDataVariables = (
	testPeriod: OptionType | undefined,
	searchValue: string | undefined,
	environmentsNoOption?: boolean
): QueryVariablesBuilder<UsageDataQueryVariables> => {
	const variablesBuilder = new QueryVariablesBuilder<UsageDataQueryVariables>({
		searchFields: testsSearchFields
	});

	variablesBuilder.addTextSearchParam(searchValue ?? '');

	if (environmentsNoOption) {
		variablesBuilder.addConditionParam('environmentsEqAnyItem', '');
	}

	if (testPeriod?.value && testPeriod.value !== 'all' && typeof testPeriod.value === 'number') {
		variablesBuilder
			.addConditionParam(DAYS_AMOUNT_FILTER_PARAM_KEY, testPeriod.value)
			.addConditionParam(DAYS_AMOUNT_X2_FILTER_PARAM_KEY, 2 * testPeriod.value);
	}

	return variablesBuilder;
};

export const buildUsageDataVariables = (
	testPeriod?: OptionType,
	searchValue?: string,
	labels?: OptionType[]
): UsageDataQueryVariables => {
	const { sanitizedLabels, environmentsNoOption } = separateNoOptionLabels(labels);
	const variablesBuilder = buildBaseUsageDataVariables(
		testPeriod,
		searchValue,
		environmentsNoOption
	);

	if (sanitizedLabels.length) {
		variablesBuilder.addTextSearchParam(
			buildLabelsSearchValues(sanitizedLabels),
			labelsSearchFields,
			true
		);
	}

	const variables = variablesBuilder.getVariables();

	return variables;
};

export const buildUsageDataVariablesV2 = (
	testPeriod?: OptionType,
	searchValue?: string,
	labels?: OptionType[]
): UsageDataQueryVariables => {
	const { sanitizedLabels, environmentNeTupleKey, unitIdNeTupleKey, environmentsNoOption } =
		separateNoOptionLabels(labels);
	const variablesBuilder = buildBaseUsageDataVariables(
		testPeriod,
		searchValue,
		environmentsNoOption
	);

	const filterVariables = sanitizedLabels.length
		? buildFilterVariables(parseLabelsIntoFilters(sanitizedLabels))
		: {};

	const baseVariables = variablesBuilder.getVariables();

	const result: UsageDataQueryVariables = {
		...baseVariables,
		...filterVariables
	};

	return addSeparatedCustomFieldsNeTupleKeys(result, environmentNeTupleKey, unitIdNeTupleKey);
};
